import React, { useState, useRef, useEffect } from "react";
import moment from "moment";
import Modal from "@mui/material/Modal";
import { postRequest, getRequest, showMessage } from "../../../../../../config";

const PRIORITY_OPTIONS = ["High Priority", "Medium Priority", "Low Priority", "N/A"];
const STATUS_OPTIONS = ["N/A", "Not Started", "Started", "In Progress", "Completed - Further Work", "Completed - No Issues"];

const PRIORITY_STYLES = {
    "High Priority": { background: "#EB5A46" },
    "Medium Priority": { background: "#FD9E31" },
    "Low Priority": { background: "#F1D52F" },
    "N/A": { background: "#BBBBBB" },
};

const STATUS_STYLES = {
    "N/A": { background: "#BBBBBB" },
    "Not Started": { background: "#87DDEC" },
    "Started": { background: "#20A5DE" },
    "In Progress": { background: "#F1D52F" },
    "Completed - Further Work": { background: "#FD9E31" },
    "Completed - No Issues": { background: "#35E586" },
};

export default function WorkflowNotesModal({ open, onClose, question, section, answer, user, company, module, onAnswerUpdated }) {
    const [noteText, setNoteText] = useState("");
    const [saving, setSaving] = useState(false);
    const [localAnswer, setLocalAnswer] = useState(answer);
    const [hoveredNoteIdx, setHoveredNoteIdx] = useState(null);
    const textareaRef = useRef(null);

    // Re-sync local state whenever the modal is opened with fresh answer data
    useEffect(() => {
        if (open) setLocalAnswer(answer);
    }, [open]);

    const answerId = localAnswer?._id;
    const moduleName = module?.type;
    const companyId = company?._id;

    const currentPriority = localAnswer?.taskPriority || null;
    const currentStatus = localAnswer?.taskStatus || null;

    const callAddNote = async (payload) => {
        if (!answerId) { showMessage("Answer not saved yet — please answer the question first."); return null; }
        const res = await postRequest(`/question/addNote/${answerId}`, { ...payload, companyId, moduleName });
        if (res?.code === 200) {
            if (res.body) setLocalAnswer(res.body);
            onAnswerUpdated(res.body);
            showMessage(res.message);
        } else {
            showMessage(res?.message || "Something went wrong");
        }
        return res;
    };

    const handleSaveNote = async () => {
        const trimmed = noteText.trim();
        if (!trimmed) { showMessage("Cannot save an empty note!"); return; }
        setSaving(true);
        await callAddNote({ taskNotes: [{ note: trimmed }] });
        setNoteText("");
        setSaving(false);
    };

    const handlePriority = async (value) => {
        setSaving(true);
        await callAddNote({ taskPriority: value });
        setSaving(false);
    };

    const handleStatus = async (value) => {
        setSaving(true);
        await callAddNote({ taskStatus: value });
        setSaving(false);
    };

    const handleDeleteNote = async (noteId) => {
        if (!answerId) return;
        setSaving(true);
        const res = await getRequest(`/question/deleteTaskNote/${answerId}/${noteId}`);
        if (res?.code === 200) {
            if (res.body) setLocalAnswer(res.body);
            onAnswerUpdated(res.body);
            showMessage(res.message);
        } else {
            showMessage(res?.message || "Something went wrong");
        }
        setSaving(false);
    };

    const notes = [...(localAnswer?.taskNotes || [])].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    return (
        <Modal open={open} onClose={onClose} className="backdrop td500 zi4 pf t0 l0 w100vw h100vh df aic jcc">
            <>
                <div className="bg1 br20px shadow p1rem" style={{ width: "min(920px, 195vw)", maxHeight: "85vh", display: "flex", flexDirection: "column", outline: "none" }}>

                    {/* Header */}
                    <div className="df aic jcsb px1-5rem pt1-5rem pb1rem">
                        <div>
                            <p className="fs1-5rem c15 mb0-25rem fw400">Workflow notes:</p>
                            <p className="fs1rem fw300 c3 mb0">{module?.heading || module?.type} - {question?.qNo} {section?.sectionName || section?.name || ""}</p>
                        </div>
                        <div className="df aic gg0-5rem fww">
                            {/* Priority dropdown */}
                            <div className="menuHover pr">
                                <button disabled={saving} className={`df aic px1rem py0-25rem fs12px fw500 cp bg5 br20px bw1px bss bo16 ${currentPriority ? 'c1' : 'c15'}`} style={currentPriority && { ...PRIORITY_STYLES[currentPriority], border: "1px solid currentColor" }}>
                                    {currentPriority || "Priority"}
                                    <svg className="ml0-5rem" width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                </button>
                                <div className="menu op0 pen pa bg1 shadow br10px zi10" style={{ top: "25px", right: 0, minWidth: "160px", overflow: "hidden" }}>
                                    {PRIORITY_OPTIONS.map(opt => (
                                        <button key={opt} onClick={() => handlePriority(opt)} className="w100 df aic px1rem py0-5rem cp c1 fs12px fw500" style={{ ...PRIORITY_STYLES[opt] }}>{opt}</button>
                                    ))}
                                </div>
                            </div>

                            {/* Status dropdown */}
                            <div className="menuHover pr">
                                <button disabled={saving} className={`df aic px1rem py0-25rem fs12px fw500 cp br20px bw1px bss bo16 ${currentStatus ? 'c1' : 'c15'}`} style={currentStatus && { ...STATUS_STYLES[currentStatus], border: "1px solid currentColor" }}>
                                    {currentStatus || "Status"}
                                    <svg className="ml0-5rem" width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                </button>
                                <div className="menu op0 pen pa bg1 shadow br10px zi10" style={{ top: "25px", right: 0, minWidth: "195px", overflow: "hidden" }}>
                                    {STATUS_OPTIONS.map(opt => (
                                        <button key={opt} onClick={() => handleStatus(opt)} className="w100 df aic px1rem py0-5rem cp c1 fs12px fw500" style={{ ...STATUS_STYLES[opt] }}>{opt}</button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="oya px1-5rem py1rem" style={{ flex: 1 }}>
                        {/* Add note textarea */}
                        <div className="bss bw1px bo5 br10px df aic px0-75rem py0-5rem mb1rem">
                            <span className="c3 fw400 fs1-5rem mx0-5rem mb2rem">+</span>
                            <textarea
                                ref={textareaRef}
                                rows={3}
                                placeholder="Add workflow note"
                                value={noteText}
                                onChange={e => setNoteText(e.target.value)}
                                onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSaveNote(); } }}
                                className="w100 fs15px c15 pt1rem"
                            />
                            {noteText.trim() && (
                                <button onClick={handleSaveNote} disabled={saving}
                                    className="df aic jcc br50px cp c1 bg3 ml0-5rem"
                                    style={{ width: "28px", height: "28px", flexShrink: 0, border: "none", marginTop: "2px" }}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M5 12h14M12 5l7 7-7 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                </button>
                            )}
                        </div>

                        {/* Notes list */}
                        {notes.length > 0
                            ? <div className="df fdc gg0-75rem py0-5rem bg5 br10px mah30vh oa">
                                {notes.map((item, idx) => {
                                    const hasProfile = !!item?.user?.profile;
                                    const firstName = item?.user?.firstName || "";
                                    const lastName = item?.user?.lastName || "";
                                    const initials = (firstName[0] || "") + (lastName[0] || "") || "?";
                                    const fullName = firstName ? `${firstName} ${lastName}`.trim() : "Guest User";
                                    return (
                                        <div key={idx} className="df aic aifs gg0-75rem py0-75rem px1rem pr" onMouseEnter={() => setHoveredNoteIdx(idx)} onMouseLeave={() => setHoveredNoteIdx(null)}>
                                            {hasProfile
                                                ? <img src={item.user.profile} className="w30px h30px br50px mr0-5rem" alt={fullName} />
                                                : <span className="df jcc aic w30px h30px br50px mr0-5rem fw500 fs12px c1 bg3">{initials}</span>
                                            }
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <span className="fs13px fw600 c15">{fullName} </span>
                                                <span className="fs13px c15 fw400">{item.note}</span>
                                                <p className="fs11px mt0-25rem mb0" style={{ color: "#9ca3af" }}>{moment(item.createdAt).format("MMM D, YYYY, h:mmA")}</p>
                                            </div>
                                            {hoveredNoteIdx === idx && (
                                                <button onClick={() => handleDeleteNote(item._id)} disabled={saving} className="pa cp df aic jcc br50px bg1 bw1px bss bo16" style={{ top: '10px', right: '20px' }}>
                                                    <i className="fa fa-trash-o c23" />
                                                </button>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                            : <p className="tac fs13px c12 mt1rem">No notes yet. Add the first one above.</p>
                        }
                    </div>
                </div>
                <svg className="pa cp" onClick={onClose} style={{ left: '50%', transform: 'translateX(-50%)', bottom: '12px' }} width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect opacity="0.8" x="4" y="4" width="52" height="52" rx="26" fill="#151313" />
                    <path d="M48.5626 11.4375C44.8913 7.76665 40.214 5.26685 35.1221 4.2542C30.0302 3.24156 24.7523 3.76155 19.9559 5.74843C15.1595 7.73531 11.06 11.0998 8.17569 15.4166C5.29142 19.7333 3.75195 24.8084 3.75195 30C3.75195 35.1917 5.29142 40.2667 8.17569 44.5835C11.06 48.9002 15.1595 52.2647 19.9559 54.2516C24.7523 56.2385 30.0302 56.7585 35.1221 55.7458C40.214 54.7332 44.8913 52.2334 48.5626 48.5625C51.0004 46.1249 52.9342 43.231 54.2535 40.0461C55.5729 36.8611 56.252 33.4474 56.252 30C56.252 26.5526 55.5729 23.1389 54.2535 19.954C52.9342 16.769 51.0004 13.8751 48.5626 11.4375ZM39.3751 36.6375C39.5624 36.8129 39.7117 37.0249 39.8138 37.2603C39.9159 37.4958 39.9686 37.7496 39.9686 38.0063C39.9686 38.2629 39.9159 38.5168 39.8138 38.7522C39.7117 38.9876 39.5624 39.1996 39.3751 39.375C39.1999 39.5488 38.9921 39.6863 38.7636 39.7796C38.5352 39.8729 38.2906 39.9202 38.0438 39.9188C37.5519 39.9167 37.0805 39.7214 36.7313 39.375L30.0001 32.6438L23.3626 39.375C23.0133 39.7214 22.5419 39.9167 22.0501 39.9188C21.8033 39.9202 21.5587 39.8729 21.3302 39.7796C21.1018 39.6863 20.894 39.5488 20.7188 39.375C20.3696 39.0237 20.1736 38.5485 20.1736 38.0531C20.1736 37.5578 20.3696 37.0826 20.7188 36.7313L27.3563 30L20.7188 23.3625C20.4116 23.0038 20.2511 22.5424 20.2694 22.0705C20.2876 21.5986 20.4832 21.151 20.8171 20.8171C21.1511 20.4832 21.5987 20.2875 22.0706 20.2693C22.5425 20.2511 23.0039 20.4116 23.3626 20.7188L30.0001 27.3563L36.6376 20.7188C36.8111 20.539 37.0184 20.3952 37.2476 20.2956C37.4768 20.196 37.7233 20.1424 37.9732 20.1381C38.223 20.1337 38.4713 20.1786 38.7038 20.2702C38.9363 20.3618 39.1484 20.4983 39.3282 20.6719C39.5079 20.8455 39.6517 21.0528 39.7514 21.2819C39.851 21.5111 39.9045 21.7576 39.9089 22.0075C39.9132 22.2573 39.8683 22.5056 39.7767 22.7381C39.6851 22.9706 39.5486 23.1828 39.3751 23.3625L32.6438 30L39.3751 36.6375Z" fill="white" />
                </svg>
            </>
        </Modal>
    );
}
